import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../config/api';
import { addNotification } from '../../store/slices/uiSlice';
import { useDispatch } from 'react-redux';
import { MEMBERSHIP_SELECT_CLASS } from '../../utils/orgMembershipUtils';

const emptyDraft = {
  name: '',
  reportsToRoleId: '',
  sequence: '',
  canSeeSpaces: false,
};

const buildRoleForest = (roles = []) => {
  const ids = new Set(roles.map((role) => role.id));
  const byParent = new Map();
  roles.forEach((role) => {
    const parentId = role.reportsToRoleId && ids.has(role.reportsToRoleId) ? role.reportsToRoleId : '';
    if (!byParent.has(parentId)) byParent.set(parentId, []);
    byParent.get(parentId).push(role);
  });
  byParent.forEach((list) => {
    list.sort((a, b) => (b.sequence || 0) - (a.sequence || 0) || String(a.name || '').localeCompare(String(b.name || '')));
  });
  return byParent;
};

const RoleTreeNode = ({ role, childrenByParent, depth = 0, onEdit }) => {
  const children = childrenByParent.get(role.id) || [];
  return (
    <li className="relative">
      {depth > 0 ? (
        <span className="absolute left-[-18px] top-4 w-4 border-t border-gray-300 dark:border-gray-600" />
      ) : null}
      <button
        type="button"
        onClick={() => onEdit?.(role)}
        className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-primary-400 dark:hover:border-primary-500"
      >
        <span className="inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-200 text-xs font-semibold">
          {role.sequence}
        </span>
        <span className="text-sm font-medium text-gray-900 dark:text-white">{role.name}</span>
        {role.canSeeSpaces ? (
          <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
            Spaces
          </span>
        ) : null}
      </button>
      {children.length ? (
        <ul className="mt-2 ml-6 pl-4 border-l border-gray-300 dark:border-gray-600 space-y-2">
          {children.map((child) => (
            <RoleTreeNode
              key={child.id}
              role={child}
              childrenByParent={childrenByParent}
              depth={depth + 1}
              onEdit={onEdit}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
};

const OrganizationRolesPanel = ({ token }) => {
  const dispatch = useDispatch();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };
  const childrenByParent = useMemo(() => buildRoleForest(roles), [roles]);
  const topRoles = childrenByParent.get('') || [];

  const loadRoles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/organization-roles`, { headers });
      setRoles(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Failed to load organization roles',
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, [token]);

  const resetDraft = () => {
    setEditingId(null);
    setDraft(emptyDraft);
  };

  const startEdit = (role) => {
    setEditingId(role.id);
    setDraft({
      name: role.name || '',
      reportsToRoleId: role.reportsToRoleId || '',
      sequence: String(role.sequence || ''),
      canSeeSpaces: Boolean(role.canSeeSpaces),
    });
  };

  const saveRole = async (event) => {
    event.preventDefault();
    const name = String(draft.name || '').trim();
    if (!name) {
      dispatch(addNotification({ type: 'error', message: 'Role name is required' }));
      return;
    }
    const body = {
      name,
      reportsToRoleId: draft.reportsToRoleId || '',
      canSeeSpaces: Boolean(draft.canSeeSpaces),
    };
    if (String(draft.sequence || '').trim()) {
      body.sequence = Number(draft.sequence);
    }
    try {
      setSaving(true);
      if (editingId) {
        await axios.patch(`${API_URL}/organization-roles/${editingId}`, body, { headers });
      } else {
        await axios.post(`${API_URL}/organization-roles`, body, { headers });
      }
      dispatch(addNotification({
        type: 'success',
        message: editingId ? 'Role updated' : 'Role added',
      }));
      resetDraft();
      await loadRoles();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Failed to save role',
      }));
    } finally {
      setSaving(false);
    }
  };

  const deleteRole = async (role) => {
    if (!window.confirm(`Delete ${role.name}? People on this role must be reassigned first.`)) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/organization-roles/${role.id}`, { headers });
      dispatch(addNotification({ type: 'success', message: 'Role deleted' }));
      if (editingId === role.id) resetDraft();
      await loadRoles();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Failed to delete role',
      }));
    }
  };

  const parentOptions = roles.filter((role) => role.id !== editingId);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Organization roles</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Who sits under whom is the ranking. Roles closer to org admin are higher and can assign supervisors to roles below them. Sequence only orders siblings in the tree.
        </p>
      </div>
      <form onSubmit={saveRole} className="p-6 border-b border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="text-sm text-gray-700 dark:text-gray-300">
          Role name
          <input
            value={draft.name}
            onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
            className={MEMBERSHIP_SELECT_CLASS}
            placeholder="Team lead"
          />
        </label>
        <label className="text-sm text-gray-700 dark:text-gray-300">
          Sits under
          <select
            value={draft.reportsToRoleId}
            onChange={(e) => setDraft((prev) => ({ ...prev, reportsToRoleId: e.target.value }))}
            className={MEMBERSHIP_SELECT_CLASS}
          >
            <option value="">Org admin (top)</option>
            {parentOptions.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700 dark:text-gray-300">
          Sequence
          <input
            type="number"
            min={1}
            value={draft.sequence}
            onChange={(e) => setDraft((prev) => ({ ...prev, sequence: e.target.value }))}
            className={MEMBERSHIP_SELECT_CLASS}
            placeholder="Auto if empty"
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mt-6">
          <input
            type="checkbox"
            checked={draft.canSeeSpaces}
            onChange={(e) => setDraft((prev) => ({ ...prev, canSeeSpaces: e.target.checked }))}
          />
          Can see Spaces
        </label>
        <div className="md:col-span-2 flex gap-2">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : editingId ? 'Save role' : 'Add role'}
          </button>
          {editingId ? (
            <button
              type="button"
              onClick={resetDraft}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
            >
              Cancel
            </button>
          ) : null}
        </div>
      </form>
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
        </div>
      ) : (
        <>
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
              Role tree
            </h3>
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4">
              <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold">
                Org admin
                <span className="text-[11px] font-normal bg-white/20 px-1.5 py-0.5 rounded-full">0</span>
              </div>
              {topRoles.length ? (
                <ul className="mt-3 ml-4 pl-4 border-l-2 border-indigo-200 dark:border-indigo-800 space-y-2">
                  {topRoles.map((role) => (
                    <RoleTreeNode
                      key={role.id}
                      role={role}
                      childrenByParent={childrenByParent}
                      depth={1}
                      onEdit={startEdit}
                    />
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Add a role to grow the tree under org admin.</p>
              )}
            </div>
          </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Under</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Sequence</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Spaces</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              <tr>
                <td className="px-6 py-3 text-sm font-medium text-gray-900 dark:text-white">Org admin</td>
                <td className="px-6 py-3 text-sm text-gray-500">—</td>
                <td className="px-6 py-3 text-sm text-gray-500">0</td>
                <td className="px-6 py-3 text-sm text-gray-500">Yes</td>
                <td />
              </tr>
              {roles.map((role) => (
                <tr key={role.id}>
                  <td className="px-6 py-3 text-sm font-medium text-gray-900 dark:text-white">{role.name}</td>
                  <td className="px-6 py-3 text-sm text-gray-600 dark:text-gray-300">{role.reportsToRoleName || 'Org admin'}</td>
                  <td className="px-6 py-3 text-sm text-gray-600 dark:text-gray-300">{role.sequence}</td>
                  <td className="px-6 py-3 text-sm text-gray-600 dark:text-gray-300">{role.canSeeSpaces ? 'Yes' : 'No'}</td>
                  <td className="px-6 py-3 text-right text-sm space-x-2">
                    <button type="button" onClick={() => startEdit(role)} className="text-primary-600 hover:text-primary-700">
                      Edit
                    </button>
                    <button type="button" onClick={() => deleteRole(role)} className="text-red-600 hover:text-red-700">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
};

export default OrganizationRolesPanel;
